import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Bolton\'s RED DfT rating guarantee my claim will succeed?',
    answer: 'No. A RED overall rating is official evidence that the Department for Transport assessed Bolton\'s highways maintenance performance as among the weakest in England — one of 13 red-rated authorities in 2025/26. Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The rating strengthens context; your photos, prior reports and road classification do the rest.',
  },
  {
    question: 'Bolton surveys U-roads annually — does that weaken a residential-street claim?',
    answer: 'Not necessarily. The council\'s transparency report confirms unclassified roads are surveyed annually using visual inspection by qualified technicians — not the SCANNER laser surveys used on A, B and C roads. Visual surveys produce a RED-category percentage only; amber and green splits are not published for U-roads. At 34% RED in 2024/25 — up from 21% the year before — the council\'s own data shows residential roads deteriorating far faster than classified routes at 1.7–2.7% RED.',
  },
  {
    question: 'What if my pothole was on an A or B road in Bolton?',
    answer: 'A and B/C roads receive annual SCANNER surveys. In 2024, A-roads were 1.7% RED and B/C roads 2.7% RED — much lower than U-roads at 34% RED. The council still needs to prove it conducted the survey on schedule, identified your defect within response timeframes, and repaired it appropriately. A professional claim pack ensures you ask for inspection and repair records, not just network averages.',
  },
  {
    question: 'Why is Bolton RED on spend when 72.6% of maintenance was preventative in 2024/25?',
    answer: 'The DfT Spend scorecard is separate from the council\'s own preventative/reactive split. Bolton earned RED on spend alongside AMBER on condition and best practice. Its transparency report shows £4.69m capital spend against a £3.462m DfT allocation in 2024/25, yet only 13.8 miles of preventative carriageway work across 632 miles of roads — roughly 2.2% of the network. Aggregate percentages do not prove the individual pothole was caught within inspection intervals.',
  },
  {
    question: 'Bolton defines potholes as 40mm deep — does a shallower defect block my claim?',
    answer: 'The 40mm threshold (25mm on pedestrian crossing points) is Bolton\'s repair standard, not a liability threshold. The council states defects below that may be recorded as planned repairs rather than urgent pothole fills — which is why the published pothole count understates total surface defects. A shallower defect can still have caused your damage; photographic evidence of depth and prior reports matter more than whether Bolton counted it as a pothole.',
  },
  {
    question: '10,263 pothole fills in 2024/25 — does falling from the 2022/23 peak mean roads are improving?',
    answer: 'Not reliably. Fills fell from a peak of 15,568 in 2022/23 to 10,263 in 2024/25, but the council projects ~11,500 fills in 2025/26 and U-road RED condition rose to 34% in 2024/25. Lower fill counts can reflect reporting definitions, weather, or staffing — not proof your road was maintained to a reasonable standard. Reactive volume alongside rising U-road RED scores suggests ongoing deterioration on the network where most claims occur.',
  },
  {
    question: 'How do I report a pothole to Bolton Council?',
    answer: 'Report highways defects via Bolton\'s online Highways Reporting Tool at highwaysreporting.bolton.gov.uk, or through the council\'s potholes and road problems page at bolton.gov.uk. Defects posing an immediate safety risk should be reported by phone: 01204 336600 (Mon–Fri 8am–4pm) or 01204 336900 outside those hours. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident.',
  },
];

export function BoltonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bolton Pothole Claims | 34% of U-Roads RED — DfT RED Rating | Fixtyer"
        description="Bolton Council earns a RED DfT rating with 34% of unclassified roads in RED condition, 10,263 pothole fills in 2024/25, and a RED spend scorecard. See what the published data means for your Section 58 claim."
        canonical="/councils/bolton"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bolton', url: '/councils/bolton' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Bolton Council"
        councilSlug="bolton"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/bolton"
        headline="Bolton Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bolton</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            34% of Bolton's Residential Roads in RED Condition — 10,263 Potholes Filled in 2024/25
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Bolton Metropolitan Borough Council is one of{' '}
            <strong className="text-rose-400">13 red-rated authorities in England</strong> for 2025/26. Its own
            transparency report records <strong className="text-rose-400">34% of unclassified roads in RED condition</strong>{' '}
            in 2024/25 — up from 21% the year before — while A and B/C roads sit at 1.7% and 2.7% RED under annual
            SCANNER surveys. The council filled <strong className="text-amber-400">10,263 potholes in 2024/25</strong>{' '}
            across 822km of U-roads that make up 80% of the network. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">34%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024/25)</div>
            <div className="text-brand-300 text-sm mt-1">
              Bolton's own visual surveys — not SCANNER — show a third of residential and estate roads exceeded the
              point where structural repair should be considered. Classified roads: under 3% RED.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Department for Transport scorecard: RED overall
          </h2>
          <p className="text-surface-600 mb-8">
            Four separate ratings from the 2025/26 Local Road Maintenance Ratings — not a single headline figure
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center">
              <RatingBadge rating="red" size="md" />
              <div className="text-sm font-semibold text-rose-900 mt-3">Overall</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Condition</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center">
              <RatingBadge rating="red" size="md" />
              <div className="text-sm font-semibold text-rose-900 mt-3">Spend</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Best Practice</div>
            </div>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-3">What RED overall means</h3>
            <p className="text-rose-800 text-sm mb-3">
              Bolton is one of 13 local highway authorities in England rated RED for 2025/26 — alongside authorities
              such as Bedford, Derbyshire, Greenwich and Suffolk. The Department for Transport states that red-rated
              authorities will receive a dedicated peer-review support programme.
            </p>
            <p className="text-rose-800 text-sm">
              RED does not automatically mean your claim succeeds. It is official confirmation that network-level
              maintenance performance — across condition, spend and best practice — fell below the DfT threshold.
              Your claim still depends on evidence about the specific defect.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,017km of roads — 822km unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network composition from Bolton Council's 2025 transparency report — where most pothole claims start
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,017km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">822km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-rose-700 mt-1">80% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">107km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Annual SCANNER surveys</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">88km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
              <div className="text-xs text-surface-500 mt-1">Annual SCANNER surveys</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Bolton highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">1,407km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Other public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">367km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">29km</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Highway structures, streetlights, gullies</td>
                  <td className="px-6 py-4 text-center font-medium">360 structures; 36,209 streetlights; 65,500 gullies</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Bolton developed rapidly in the 18th and 19th centuries with the development of many industries. Much of the highway infrastructure dates from this period when the first large-scale commercial and residential development took place. As a result, a large portion of the road network has not been constructed to modern standards and therefore is prone to deterioration."
            source="Bolton Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Two survey systems, two very different pictures
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER laser surveys on classified roads; annual visual surveys on U-roads — with only RED percentages published
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Bolton's transparency report publishes full red/amber/green breakdowns
              for A and B/C roads from SCANNER surveys. For unclassified roads, only the percentage in RED category is
              published — not amber or green splits. U-roads are surveyed annually using visual inspection by qualified
              technicians who record major and minor defects. Safety inspections with walked inspections also run on a
              hierarchy basis — in some cases monthly.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (107km) — annual SCANNER surveys</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.6%</td>
                    <td className="px-4 py-2 text-center">13.6%</td>
                    <td className="px-4 py-2 text-center">82.8%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">1.7%</td>
                    <td className="px-4 py-2 text-center font-bold">12.5%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">85.8%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Classified A-roads have remained around 2–4% RED for years, with direct central-government funding keeping
              strategic routes in relatively stable condition — currently 85.8% green-rated.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (88km) — annual SCANNER surveys</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.5%</td>
                    <td className="px-4 py-2 text-center">14.4%</td>
                    <td className="px-4 py-2 text-center">81.1%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">2.7%</td>
                    <td className="px-4 py-2 text-center font-bold">15.3%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">82.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* U roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (822km) — annual visual surveys</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red (% published)</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-500">Amber / Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Not published</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Not published</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Not published</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Not published</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">34%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Not published</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              At 34% RED, roughly 279km of Bolton's residential and estate network exceeded the council's own threshold
              for considering structural repair — more than ten times the RED rate on classified A-roads. RED condition
              jumped 13 percentage points year-on-year from 21% in 2023/24.
            </p>
          </div>

          <QuoteCard
            quote="Unclassified roads are surveyed annual utilising a visual survey undertaken by qualified technicians. They record major and minor defects which are then used to score the condition of sections of each road. This data is then used to formulate a programme of works for the following year."
            source="Bolton Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            10,263 pothole fills in 2024/25
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Reactive repair volumes from the council's own transparency report — defects over 40mm deep on carriageways
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Bolton estimated potholes filled 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Potholes filled</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Change vs prior year</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">4,552</td>
                  <td className="px-4 py-3 text-center text-brand-400">—</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">10,699</td>
                  <td className="px-4 py-3 text-center text-amber-400">+135%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3 font-medium">2022/23</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">15,568</td>
                  <td className="px-4 py-3 text-center text-rose-400">Peak</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">11,964</td>
                  <td className="px-4 py-3 text-center text-amber-400">−23%</td>
                </tr>
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">10,263</td>
                  <td className="px-4 py-3 text-center text-amber-400">−34% from peak</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">~11,500</td>
                  <td className="px-4 py-3 text-center text-brand-400">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Bolton Council defines a pothole as being within a carriageway and over 40mm deep or over 25mm deep on a pedestrian crossing point. Any defects not meeting the above requirements may not be recorded as a pothole and may result in the repair of a defect being planned rather than an urgent repair."
              source="Bolton Council Local Highways Maintenance Transparency Report 2025"
            />
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Nearly 28 patches a day</h3>
              <p className="text-brand-200 text-sm">
                10,263 pothole fills in 2024/25 works out to roughly 28 urgent repairs per day across 1,017km — while
                U-road RED condition rose to 34%. The published count captures only defects meeting the 40mm threshold;
                shallower defects may be scheduled as planned repairs instead.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            RED spend scorecard despite 72.6% preventative share
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue spending from Bolton's transparency report — carriageways only
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Bolton highway maintenance spending</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-brand-50">
                  <td className="px-4 py-2 font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-2 text-center">£4.794m</td>
                  <td className="px-4 py-2 text-center font-bold">£5.055m</td>
                  <td className="px-4 py-2 text-center">£1.664m</td>
                  <td className="px-4 py-2 text-center text-emerald-600">74.2%</td>
                  <td className="px-4 py-2 text-center">25.8%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-4 py-2 font-bold">2024/25</td>
                  <td className="px-4 py-2 text-center">£3.462m</td>
                  <td className="px-4 py-2 text-center font-bold">£4.69m</td>
                  <td className="px-4 py-2 text-center">£1.773m</td>
                  <td className="px-4 py-2 text-center text-emerald-600">72.6%</td>
                  <td className="px-4 py-2 text-center text-amber-600">27.4%</td>
                </tr>
                <tr>
                  <td className="px-4 py-2">2023/24</td>
                  <td className="px-4 py-2 text-center">£5.211m</td>
                  <td className="px-4 py-2 text-center">£5.089m</td>
                  <td className="px-4 py-2 text-center">£1.834m</td>
                  <td className="px-4 py-2 text-center">73.5%</td>
                  <td className="px-4 py-2 text-center">26.5%</td>
                </tr>
                <tr>
                  <td className="px-4 py-2">2022/23</td>
                  <td className="px-4 py-2 text-center">£3.127m</td>
                  <td className="px-4 py-2 text-center">£4.891m</td>
                  <td className="px-4 py-2 text-center">£2.479m</td>
                  <td className="px-4 py-2 text-center">66.4%</td>
                  <td className="px-4 py-2 text-center">33.6%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <h3 className="font-semibold text-brand-900 px-6 py-4 border-b border-surface-200">
              Preventative carriageway work delivered (miles)
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Reconstruction</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Resurfacing</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Surface treatment</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2023/24</td>
                  <td className="px-4 py-2 text-center">2.7</td>
                  <td className="px-4 py-2 text-center">6.0</td>
                  <td className="px-4 py-2 text-center">2.3</td>
                  <td className="px-4 py-2 text-center">11.0</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-2 text-center font-bold">3.2</td>
                  <td className="px-4 py-2 text-center font-bold">5.0</td>
                  <td className="px-4 py-2 text-center font-bold">5.6</td>
                  <td className="px-4 py-2 text-center font-bold">13.8</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-2 font-bold">2025/26 (planned)</td>
                  <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>
                    ~5.8 miles resurfacing + 2.4 miles surface treatment
                  </td>
                  <td className="px-4 py-2 text-center font-bold">~8.2</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="font-semibold text-amber-900 mb-3">Coverage maths</h3>
            <p className="text-amber-800 text-sm">
              Bolton manages 632 miles of carriageway. Even at 13.8 miles treated in 2024/25, that is roughly 2.2% of
              the network in a single year. The council targets a 70/30 preventative/reactive split and plans ~11,500
              pothole fills for 2025/26 — yet U-road RED condition reached 34%. Volume of reactive patching does not
              prove every defect was caught within inspection intervals on the roads where most claims happen.
            </p>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Bolton says it knows the condition of its network — and where claim evidence can focus
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> annual SCANNER condition surveys</li>
                <li>• <strong>B and C roads:</strong> annual SCANNER condition surveys</li>
                <li>• <strong>U roads:</strong> annual visual surveys by qualified technicians</li>
                <li>• <strong>Safety inspections:</strong> walked inspections on a hierarchy basis — monthly in some cases</li>
                <li>• <strong>Defect reports:</strong> public reports assessed by inspectors before reactive action</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 planned works</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• ~5.8 miles of carriageway resurfacing planned</li>
                <li>• ~2.4 miles of surface treatments planned</li>
                <li>• ~11,500 pothole fills estimated</li>
                <li>• Structure repairs at Daisy Hill Station, Temple Road and Moss Bank</li>
                <li>• Part of Greater Manchester Road Activity Permit Scheme (GMRAPS)</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Bolton must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must show
              a reasonable system for inspecting and repairing the specific defect — not just publish a maintenance
              strategy or DfT scorecard.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the annual U-road visual survey cycle — and had it been surveyed recently?</li>
              <li>• Did the defect meet the 40mm pothole threshold or fall into planned-repair category?</li>
              <li>• Were there prior reports (Highways Reporting Tool, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the last survey or safety inspection?</li>
              <li>• On a U-road at 34% network-wide RED, can the council show your section was adequately monitored?</li>
            </ul>
          </div>

          <QuoteCard
            quote="Our primary aim is to move to a planned maintenance regime rather than a largely reactive maintenance regime. By undertaking regular surveys of the highway, we can monitor the condition of assets, determine where the asset is in its lifecycle and make the most appropriate intervention at the right time to prolong its life."
            source="Bolton Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Bolton acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On asset deterioration</h3>
              <QuoteCard
                quote="All assets will deteriorate over time, the rate at which highway surfaces deteriorate depend on their construction and age. They are particularly susceptible to damage from water, cold weather, overloaded vehicles and being dug up by utility companies."
                source="Bolton Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On risk-based maintenance</h3>
              <QuoteCard
                quote="Adopting a risk-based approach to highway infrastructure maintenance is crucial. This involves prioritising maintenance activities based on the risk and impact of asset failure."
                source="Bolton Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On reactive vs planned work</h3>
              <QuoteCard
                quote="Problems on the highway are identified by highway inspectors or from reports by the public. Each report is assessed by an inspector and if an urgent repair is required this is sent to the reactive maintenance team to action."
                source="Bolton Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a RED-rated borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Bolton's DfT ratings and published data — what works for each side
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER condition scorecard — not the weakest tier on network condition alone
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Classified A and B/C roads at 1.7% and 2.7% RED under SCANNER in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented annual surveys on all road classes — visual on U-roads, SCANNER on classified
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 72.6% preventative spend share in 2024/25 with a stated 70/30 target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published Highway Asset Management Policy and Strategy (2025)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on A-roads where SCANNER data is strong and inspection records exist.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall and RED spend scorecards from the DfT for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 34% of U-roads in RED condition in 2024/25 — up from 21% year-on-year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 822km of residential network on visual surveys — no published amber/green splits
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 10,263 pothole fills in 2024/25; ~11,500 projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only ~8.2 miles of planned preventative work on 632 miles of roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Victorian-era road construction acknowledged as prone to faster deterioration
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with mixed scorecards and 34% U-road RED condition, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road type</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (Highways Reporting Tool, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 34% RED rate and visual-survey methodology are your strongest structural arguments</li>
              <li>• Whether the council can produce inspection records for your street, not just network-wide averages</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from Bolton's
              own transparency data — including the U-road deterioration trend — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Bolton Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack. Bolton only action potholes at least 40mm deep on
            carriageways (25mm on pavements or designated crossing points).
          </p>
          <a
            href="https://www.bolton.gov.uk/report-street-problem/potholes-road-pavement-problems"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — bolton.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Bolton?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED-rated borough with 34% U-road deterioration demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 34% RED deterioration cited</li>
                <li>• No prior-report search</li>
                <li>• No four-scorecard DfT breakdown</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 34% U-road RED condition documented</li>
                <li>✅ RED overall and RED spend scorecards cited</li>
                <li>✅ 10,263 pothole fills in 2024/25 referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Bolton</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bolton"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bolton Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bolton.gov.uk/downloads/file/7120/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Bolton Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
